import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Landmark, Building2, User as UserIcon, Loader2, Check, MapPin, Briefcase } from "lucide-react";

const BRAZIL_STATES = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

const DDI_OPTIONS = [
  { code: "55", label: "🇧🇷 Brasil", short: "+55" },
  { code: "351", label: "🇵🇹 Portugal", short: "+351" },
  { code: "1", label: "🇺🇸 EUA/CA", short: "+1" },
  { code: "54", label: "🇦🇷 Argentina", short: "+54" },
  { code: "56", label: "🇨🇱 Chile", short: "+56" },
  { code: "52", label: "🇲🇽 México", short: "+52" },
  { code: "598", label: "🇺🇾 Uruguai", short: "+598" },
  { code: "595", label: "🇵🇾 Paraguai", short: "+595" },
  { code: "34", label: "🇪🇸 Espanha", short: "+34" },
  { code: "44", label: "🇬🇧 Reino Unido", short: "+44" },
];

/** Normaliza o número: prefixa o DDI se ele ainda não estiver lá. */
function normalizePhone(ddi: string, phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith(ddi)) return digits;
  return ddi + digits;
}

/** Detecta o DDI usado e retorna { ddi, rest }. */
function splitPhone(phone: string | null | undefined): {
  ddi: string;
  rest: string;
} {
  const digits = (phone ?? "").replace(/\D/g, "");
  if (!digits) return { ddi: "55", rest: "" };
  // Tenta casar com um dos DDIs conhecidos pelo prefixo (maior primeiro para
  // evitar conflito entre 55 e 598, 56, etc.).
  const sorted = [...DDI_OPTIONS].sort(
    (a, b) => b.code.length - a.code.length,
  );
  for (const opt of sorted) {
    if (digits.startsWith(opt.code)) {
      return { ddi: opt.code, rest: digits.slice(opt.code.length) };
    }
  }
  // Sem DDI reconhecível — assume Brasil.
  return { ddi: "55", rest: digits };
}
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

type ContactType = "public_admin" | "empresa" | "cliente_final";

const TYPE_OPTIONS: Array<{
  key: ContactType;
  short: string;
  label: string;
  icon: any;
  accent: string;
}> = [
  {
    key: "public_admin",
    short: "B2G",
    label: "Órgão público",
    icon: Landmark,
    accent:
      "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 ring-amber-200 dark:ring-amber-900",
  },
  {
    key: "empresa",
    short: "B2B",
    label: "Empresa",
    icon: Building2,
    accent:
      "text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 ring-sky-200 dark:ring-sky-900",
  },
  {
    key: "cliente_final",
    short: "B2C",
    label: "Pessoa física",
    icon: UserIcon,
    accent:
      "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 ring-emerald-200 dark:ring-emerald-900",
  },
];

export default function QuickCreateContactDialog({
  open,
  onOpenChange,
  initialName,
  contactId,
  initialCompanyName,
  initialSector,
  initialCity,
  initialState,
  initialContactType,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Pré-preenche o nome (ex.: o que o usuário digitou no combobox). */
  initialName?: string;
  /** Se fornecido, abre em modo edição e carrega o contato pelo ID. */
  contactId?: number | null;
  /**
   * Pré-preenchem o formulário ao criar — útil quando adicionando um
   * participante e queremos herdar o órgão/empresa/cidade do contato principal.
   */
  initialCompanyName?: string | null;
  initialSector?: string | null;
  initialCity?: string | null;
  initialState?: string | null;
  initialContactType?: ContactType | null;
  /** Disparado ao criar OU ao editar — recebe o contato resultante. */
  onCreated: (contact: {
    id: number;
    name: string;
    lastName?: string | null;
    companyName?: string | null;
    contactType?: string | null;
  }) => void;
}) {
  const editing = !!contactId;
  const utils = trpc.useUtils();

  const contactQuery = trpc.contacts.get.useQuery(
    contactId ? { id: contactId } : (undefined as any),
    { enabled: open && editing },
  );

  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [ddi, setDdi] = useState("55");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [contactType, setContactType] = useState<ContactType>("empresa");
  const [companyName, setCompanyName] = useState("");
  const [sector, setSector] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  // Modo criação: reset ao abrir + pré-popula nome se vier do combobox.
  useEffect(() => {
    if (!open || editing) return;
    if (initialName) {
      const parts = initialName.trim().split(/\s+/);
      setName(parts[0] ?? "");
      setLastName(parts.slice(1).join(" "));
    } else {
      setName("");
      setLastName("");
    }
    setPhoneNumber("");
    setDdi("55");
    setEmail("");
    // Herda dados do contato principal quando criando um participante/relacionado.
    setCompanyName(initialCompanyName ?? "");
    setSector(initialSector ?? "");
    setJobTitle("");
    setCity(initialCity ?? "");
    setState(initialState ?? "");
    setContactType(initialContactType ?? "empresa");
  }, [
    open,
    editing,
    initialName,
    initialCompanyName,
    initialSector,
    initialCity,
    initialState,
    initialContactType,
  ]);

  // Modo edição: preenche com dados do contato.
  useEffect(() => {
    if (!open || !editing || !contactQuery.data) return;
    const c = contactQuery.data as any;
    setName(c.name ?? "");
    setLastName(c.lastName ?? "");
    const { ddi: d, rest } = splitPhone(c.phoneNumber);
    setDdi(d);
    setPhoneNumber(rest);
    setEmail(c.email ?? "");
    setContactType((c.contactType as ContactType) ?? "empresa");
    setCompanyName(c.companyName ?? "");
    setSector(c.sector ?? "");
    setJobTitle(c.jobTitle ?? "");
    setCity(c.city ?? "");
    setState(c.state ?? "");
  }, [open, editing, contactQuery.data]);

  const create = trpc.contacts.create.useMutation({
    onSuccess: (data: any) => {
      toast.success("Contato criado");
      utils.contacts.list.invalidate();
      onOpenChange(false);
      if (data) {
        onCreated({
          id: data.id ?? data?.[0]?.id,
          name,
          lastName: lastName || null,
          companyName: companyName || null,
          contactType,
        } as any);
      }
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const update = trpc.contacts.update.useMutation({
    onSuccess: (_data: any) => {
      toast.success("Contato atualizado");
      utils.contacts.list.invalidate();
      if (contactId) utils.contacts.get.invalidate({ id: contactId });
      onOpenChange(false);
      if (contactId) {
        onCreated({
          id: contactId,
          name,
          lastName: lastName || null,
          companyName: companyName || null,
          contactType,
        } as any);
      }
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const isPublic = contactType === "public_admin";
  const isCompany = contactType === "empresa";
  const isPerson = contactType === "cliente_final";

  const companyLabel = isPublic
    ? "Órgão (Prefeitura, Câmara, Autarquia...)"
    : isCompany
    ? "Nome da empresa"
    : "Empresa / organização (opcional)";

  function submit() {
    if (!name.trim()) return toast.error("Nome é obrigatório");
    const digits = phoneNumber.replace(/\D/g, "");
    // Telefone é opcional — se preenchido, precisa ser válido.
    if (digits.length > 0 && digits.length < 8) {
      return toast.error("Telefone parece inválido. Apague ou corrija.");
    }
    if (isPublic && !city.trim())
      return toast.error("Cidade do órgão é obrigatória");
    if (isPublic && !state)
      return toast.error("UF do órgão é obrigatória");
    // Sem telefone o contato vai com null: a coluna é nullable e o unique
    // (tenantId, phoneNumber) ignora NULLs, então vários contatos sem número
    // convivem. O placeholder `sem-tel-<timestamp>` que ficava aqui tinha 21
    // caracteres num varchar(20) — o insert falhava sempre que o telefone
    // estava em branco.
    const normalizedPhone = digits ? normalizePhone(ddi, phoneNumber) : null;
    const payload = {
      name: name.trim(),
      lastName: lastName.trim() || null,
      phoneNumber: normalizedPhone,
      email: email.trim() || null,
      contactType,
      companyName: companyName.trim() || null,
      sector: isPublic ? sector.trim() || null : null,
      // Cargo vale para os três tipos: existe pregoeiro em prefeitura e
      // diretor de compras em empresa. Só o setor é exclusivo de órgão.
      jobTitle: jobTitle.trim() || null,
      city: city.trim() || null,
      state: state || null,
    };
    if (editing && contactId) {
      update.mutate({ id: contactId, ...payload } as any);
    } else {
      create.mutate(payload as any);
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar contato" : "Criar contato"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Atualize os dados. As alterações valem em todo o CRM."
              : "Cadastro rápido — você poderá completar os dados depois em Contatos."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* ── Tipo ───────────────────────────── */}
          <div>
            <Label className="text-xs">Tipo de contato</Label>
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {TYPE_OPTIONS.map((t) => {
                const active = contactType === t.key;
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setContactType(t.key)}
                    className={`group flex flex-col items-center gap-1.5 rounded-md border p-3 text-sm transition ${
                      active
                        ? `ring-2 ring-offset-1 ${t.accent} border-transparent`
                        : "border-border hover:bg-muted/40"
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 ${
                        active ? "" : "text-muted-foreground"
                      }`}
                    />
                    <span className="font-semibold text-[10px] tracking-wide">
                      {t.short}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {t.label}
                    </span>
                    {active && <Check className="h-3.5 w-3.5 text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Nome + sobrenome ───────────────── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs">
                {isPerson ? "Primeiro nome *" : "Nome do contato *"}
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                className="mt-1"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs">Sobrenome</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          {/* ── Telefone + email ───────────────── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs">Telefone / WhatsApp</Label>
              <div className="mt-1 flex gap-1.5">
                <Select value={ddi} onValueChange={setDdi}>
                  <SelectTrigger className="h-9 w-[90px] shrink-0 font-mono tabular-nums">
                    <SelectValue>+{ddi}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {DDI_OPTIONS.map((opt) => (
                      <SelectItem key={opt.code} value={opt.code}>
                        <span className="flex items-center gap-2">
                          <span>{opt.label}</span>
                          <span className="font-mono text-muted-foreground text-xs">
                            {opt.short}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={ddi === "55" ? "11987654321" : "número"}
                  inputMode="tel"
                  className="flex-1"
                />
              </div>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs">E-mail</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          {/* ── Empresa / órgão ────────────────── */}
          <div>
            <Label className="text-xs">{companyLabel}</Label>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={
                isPublic
                  ? "Ex: Prefeitura Municipal de Campinas"
                  : isCompany
                  ? "Ex: Acme Tecnologia"
                  : "(opcional)"
              }
              className="mt-1"
            />
          </div>

          {isPublic && (
            <div>
              <Label className="text-xs">
                Setor / secretaria{" "}
                <span className="text-muted-foreground font-normal">
                  (opcional)
                </span>
              </Label>
              <Input
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                placeholder="Ex: Sec. de Administração (deixe vazio se não souber)"
                className="mt-1"
              />
            </div>
          )}

          {/* ── Cargo ──────────────────────────── */}
          {/* Fora do bloco isPublic de propósito: cargo é da pessoa e existe
              nos três tipos de contato — só o setor é exclusivo de órgão. */}
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Briefcase className="h-3 w-3 text-muted-foreground" />
              Cargo{" "}
              <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Input
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder={
                isPublic ? "Ex: Pregoeiro, Secretário" : "Ex: Diretor de Compras"
              }
              className="mt-1"
            />
          </div>

          {/* ── Cidade + UF ─────────────────────── */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs flex items-center gap-1">
                <MapPin className="h-3 w-3 text-muted-foreground" />
                {isPublic ? "Cidade *" : "Cidade"}
              </Label>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={isPublic ? "Ex: Campinas" : "Opcional"}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">{isPublic ? "UF *" : "UF"}</Label>
              <Select value={state || "none"} onValueChange={(v) => setState(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {BRAZIL_STATES.map((uf) => (
                    <SelectItem key={uf} value={uf}>
                      {uf}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {editing ? "Salvar alterações" : "Criar e usar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
